import { SignupSchema, useSignupForm } from "@/hooks/useSignupForm";
import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import StrongPassword from "./StrongPassword";
import { SocialSignin } from "./SocialSignin";
import { AuthenticationScreenProps, AuthenticationType } from "./types";
import { useAuthValue } from "@/contexts/contextAuth";
import { delay } from "@/libs/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { PostHogFlags } from "@/interfaces/PostHogFlags";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Zap } from "lucide-react";

export const Signup = ({ changeScreen }: AuthenticationScreenProps) => {
  const secondaryAuthButtonClass =
    "w-full border border-[#808080] bg-white text-[#212529] hover:bg-[#F7F9FC]";
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showEmailPasswordForm, setShowEmailPasswordForm] =
    useState<boolean>(false);
  const [isMagicLinkSent, setIsMagicLinkSent] = useState<boolean>(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState<boolean>(false);
  const [resendCooldown, setResendCooldown] = useState<number>(0);
  const [isCreatingAccount, setIsCreatingAccount] = useState<boolean>(false);
  const resendTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const creatingAccount = useRef(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    getValues,
    setError,
    clearErrors,
  } = useSignupForm();
  const proveVerificationEnabled = useFeatureFlagEnabled(
    PostHogFlags.ProveVerification
  );
  const { onSignup, onMagicLink, setType, setMessage, setIsShow } =
    useAuthValue();
  const passwordValue = watch("password", "");
  const email = watch("email");
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email?.trim() || "");
  const showMagicLinkConfirmation = isMagicLinkSent && !showEmailPasswordForm;

  const validateEmailField = useCallback(() => {
    const currentEmail = getValues("email")?.trim() || "";
    if (!currentEmail) {
      setError("email", { type: "manual", message: "Email is required" });
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentEmail)) {
      setError("email", { type: "manual", message: "Invalid email" });
      return false;
    }
    clearErrors("email");
    return true;
  }, [clearErrors, getValues, setError]);

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    if (resendTimer.current) {
      clearInterval(resendTimer.current);
    }
    resendTimer.current = setInterval(() => {
      setResendCooldown((current) => {
        if (current <= 1) {
          if (resendTimer.current) {
            clearInterval(resendTimer.current);
            resendTimer.current = null;
          }
          return 0;
        }
        return current - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (resendTimer.current) {
        clearInterval(resendTimer.current);
      }
    };
  }, []);

  const sendSignupMagicLink = useCallback(async () => {
    if (isSendingMagicLink || resendCooldown > 0 || !validateEmailField()) {
      return;
    }

    setIsSendingMagicLink(true);
    try {
      await onMagicLink(getValues("email").trim().toLowerCase());
      setIsMagicLinkSent(true);
      startResendCooldown();
      setType("success");
      setMessage("Check your email for your account access link");
      setIsShow(true);
    } catch {
      //
    } finally {
      setIsSendingMagicLink(false);
    }
  }, [
    getValues,
    isSendingMagicLink,
    onMagicLink,
    resendCooldown,
    setIsShow,
    setMessage,
    setType,
    startResendCooldown,
    validateEmailField,
  ]);

  const handleContinueClick = useCallback(() => {
    if (validateEmailField()) {
      setShowEmailPasswordForm(true);
    }
  }, [validateEmailField]);

  const onSubmit = useCallback(
    handleSubmit(async ({ email, password }: SignupSchema) => {
      if (creatingAccount.current) return;
      creatingAccount.current = true;
      setIsCreatingAccount(true);

      email = email.toLowerCase();
      try {
        const successUrl = await onSignup(email, password);

        window?.dataLayer &&
          window.dataLayer.push({
            event: "Account Created",
          });
        window?.fbq && window.fbq("track", "Create Account");
        window?.rdt &&
          window.rdt("track", {
            currency: "USD",
            transactionId: email,
            value: 100,
          });
        window?.uipe &&
          window.uipe("CreateAccout", {
            userEmail: email,
            name: "",
          });
        setType("success");
        setMessage(
          "You have successfully created an account, please check your email to verify your account."
        );
        setIsShow(true);
        await delay(500);
        window.location.href = successUrl;
      } catch {
        //
      } finally {
        creatingAccount.current = false;
        setIsCreatingAccount(false);
      }
    }),
    [handleSubmit, onSignup, setIsShow, setMessage, setType]
  );

  return (
    <form onSubmit={onSubmit}>
      <div>
        <h2
          className={`text-3xl text-[#212529] font-bold ${
            showEmailPasswordForm ? "mb-7" : "mb-4"
          }`}
        >
          Create your account
        </h2>

        {!showEmailPasswordForm && !isMagicLinkSent && (
          <div>
            <p className="text-xs font-normal text-[#212529] mb-7">
              By creating an account, you accept our{" "}
              <a
                className="underline"
                href="https://privateauto.com/terms"
                target="_blank"
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                className="underline"
                href="https://privateauto.com/privacy"
                target="_blank"
              >
                Privacy policy
              </a>
            </p>
            <SocialSignin action="signup" />
            <div className="flex items-center w-full my-4">
              <div className="flex-grow border-t border-[#C6C6C6]" />
              <span className="px-4 text-[#6B7280] text-sm">or</span>
              <div className="flex-grow border-t border-[#C6C6C6]" />
            </div>
            <div>
              <div className="relative">
                <input
                  type="email"
                  className="border-[#9797aa] focus:border-[#00a0c7] form-control w-full block px-4 py-2 pr-10 text-base font-medium bg-white bg-clip-padding border border-solid rounded m-0"
                  placeholder="Enter your email"
                  {...register("email")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleContinueClick();
                    }
                  }}
                />
                {isEmailValid && (
                  <CheckCircle2
                    aria-hidden="true"
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#00A878]"
                  />
                )}
              </div>
              {errors.email && (
                <div className="text-xs text-left text-[#ed0a0a] pt-2">
                  {errors.email.message}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={handleContinueClick}
              disabled={!isEmailValid}
              className="w-full bg-[#00b3de] hover:opacity-80 text-white mt-5"
            >
              Create account with password
            </Button>
            <Button
              type="button"
              variant={null}
              onClick={sendSignupMagicLink}
              disabled={
                !isEmailValid || isSendingMagicLink || resendCooldown > 0
              }
              className={`${secondaryAuthButtonClass} mt-2`}
            >
              <Zap aria-hidden="true" className="mr-2 h-4 w-4" />
              {isSendingMagicLink
                ? "Sending..."
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Send me a sign-in link instead"}
            </Button>
          </div>
        )}

        {showMagicLinkConfirmation && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F7F2]">
              <CheckCircle2
                aria-hidden="true"
                className="h-5 w-5 text-[#00A878]"
              />
            </div>
            <h3 className="text-xl font-bold text-[#212529]">
              Check your email
            </h3>
            <p className="mt-2 text-sm text-[#4B5563]">
              We sent an account access link to{" "}
              <span className="font-semibold text-[#212529]">
                {getValues("email")}
              </span>
              .
            </p>
            <Button
              type="button"
              variant={null}
              onClick={sendSignupMagicLink}
              disabled={isSendingMagicLink || resendCooldown > 0}
              className={`${secondaryAuthButtonClass} mt-5`}
            >
              {isSendingMagicLink
                ? "Resending..."
                : resendCooldown > 0
                ? `Resend in ${resendCooldown}s`
                : "Resend magic link"}
            </Button>
            <Button
              type="button"
              variant={null}
              onClick={() => {
                setIsMagicLinkSent(false);
                setShowEmailPasswordForm(false);
              }}
              className={`${secondaryAuthButtonClass} mt-2`}
            >
              Use a different email
            </Button>
            <Button
              type="button"
              variant={null}
              onClick={() => setShowEmailPasswordForm(true)}
              className={`${secondaryAuthButtonClass} mt-2`}
            >
              Create account with password instead
            </Button>
          </div>
        )}

        {showEmailPasswordForm && (
          <div>
            <div>
              <input
                type="email"
                className="border-[#9797aa] focus:border-[#00a0c7] form-control w-full block px-4 py-2 text-base font-medium bg-white bg-clip-padding border border-solid rounded m-0"
                placeholder="Enter your email"
                {...register("email")}
              />
              {errors.email && (
                <div className="text-xs text-left text-[#ed0a0a] pt-2">
                  {errors.email.message}
                </div>
              )}
            </div>
            <div className="relative mt-4">
              <input
                type={showPassword ? "text" : "password"}
                className="border-[#9797aa] focus:border-[#00a0c7] form-control w-full block px-4 py-2 text-base font-medium bg-white bg-clip-padding border border-solid rounded m-0"
                placeholder="Enter your password"
                {...register("password")}
              />
              {errors.password && (
                <div className="text-xs text-left text-[#ed0a0a] pt-2">
                  {errors.password?.message}
                </div>
              )}
              <StrongPassword password={passwordValue} />
              {showPassword ? (
                <Image
                  className="absolute top-3 right-3 cursor-pointer"
                  width={17}
                  height={15}
                  src={`/assets/eyeCrossedOut.svg`}
                  alt="eye"
                  onClick={() => setShowPassword(!showPassword)}
                />
              ) : (
                <Image
                  className="absolute top-4 right-3 cursor-pointer"
                  width={15}
                  height={11}
                  src={`/assets/eye.svg`}
                  alt="eye"
                  onClick={() => setShowPassword(!showPassword)}
                />
              )}
            </div>
            <Button
              type="submit"
              disabled={isCreatingAccount}
              className="w-full bg-[#00b3de] hover:opacity-80 text-white mt-5"
            >
              {isCreatingAccount ? "Creating account..." : "Create account"}
            </Button>
            {proveVerificationEnabled && (
              <div className="pt-5 px-4 pb-8 flex justify-center">
                <p className="text-[#808080] text-xs font-medium text-center w-[250px]">
                  By clicking “Create account” your accept our{" "}
                  <a
                    href="https://privateauto.com/terms"
                    target="_blank"
                    className="underline font-bold"
                  >
                    Terms and conditions
                  </a>
                </p>
              </div>
            )}
          </div>
        )}

        <div className="text-lg text-[#212529] font-medium py-2 text-center mt-4">
          Already have an account?{" "}
          <span
            className="text-[#00b3de] underline cursor-pointer"
            role="link"
            onClick={() => changeScreen(AuthenticationType.Signin)}
          >
            Login
          </span>
        </div>
      </div>
    </form>
  );
};
